// F1 Data Filter Module
// Handles filtering and utility functions for F1 data

/**
 * Filter F1 data by various criteria
 * @param {Object} data - Loaded F1 data
 * @param {Object} filters - Filter criteria
 * @returns {Object} - Filtered data
 */
export const filterF1Data = (data, filters = {}) => {
  if (!data) return data;

  const result = { ...data };

  // Filter by year
  if (filters.year) {
    const targetYear = parseInt(filters.year);

    // Get race IDs for the target year
    const raceIdsInYear = new Set();
    if (data.races) {
      data.races.forEach(race => {
        const raceYear = parseInt(race.year || (race.date && race.date.split('-')[0]));
        if (raceYear === targetYear) {
          raceIdsInYear.add(race.raceId);
        }
      });
    }

    // Filter races by year
    if (result.races) {
      result.races = result.races.filter(race => {
        const raceYear = parseInt(race.year || (race.date && race.date.split('-')[0]));
        return raceYear === targetYear;
      });
    }

    // Filter driver_standings by race IDs in the target year
    if (result.driver_standings) {
      result.driver_standings = result.driver_standings.filter(standing =>
        raceIdsInYear.has(standing.raceId)
      );
    }

    // Filter constructor_standings by race IDs in the target year
    if (result.constructor_standings) {
      result.constructor_standings = result.constructor_standings.filter(standing =>
        raceIdsInYear.has(standing.raceId)
      );
    }

    // Recalculate driver points based on filtered standings
    if (result.drivers && result.driver_standings) {
      const driverPoints = {};
      result.driver_standings.forEach(standing => {
        const driverId = standing.driverId;
        const points = standing.points || 0;
        // Take maximum points (cumulative championship standings)
        if (!driverPoints[driverId] || points > driverPoints[driverId]) {
          driverPoints[driverId] = points;
        }
      });

      // Get driver IDs that participated in the target year
      const participatingDriverIds = new Set(
        result.driver_standings.map(s => s.driverId)
      );

      // Filter drivers to only those who participated in the target year
      result.drivers = result.drivers
        .filter(driver => participatingDriverIds.has(driver.driverId))
        .map(driver => ({
          ...driver,
          points: driverPoints[driver.driverId] || 0
        }));
    }

    // Don't filter circuits - keep all circuits
    // Don't filter constructors - keep all constructors
    // Don't filter seasons - keep all seasons
  }

  // Filter by driver
  if (filters.driverId) {
    Object.keys(result).forEach(key => {
      if (Array.isArray(result[key])) {
        result[key] = result[key].filter(item => 
          item.driverId === filters.driverId || 
          item.driverId === parseInt(filters.driverId)
        );
      }
    });
  }

  // Filter by constructor
  if (filters.constructorId) {
    Object.keys(result).forEach(key => {
      if (Array.isArray(result[key])) {
        result[key] = result[key].filter(item => 
          item.constructorId === filters.constructorId || 
          item.constructorId === parseInt(filters.constructorId)
        );
      }
    });
  }

  // Filter by circuit
  if (filters.circuitId) {
    Object.keys(result).forEach(key => {
      if (Array.isArray(result[key])) {
        result[key] = result[key].filter(item => 
          item.circuitId === filters.circuitId || 
          item.circuitId === parseInt(filters.circuitId)
        );
      }
    });
  }

  return result;
};

/**
 * Get F1 championship statistics
 * @param {Object} data - Loaded F1 data
 * @returns {Object} - Processed statistics
 */
export const getF1ChampionshipStats = (data) => {
  if (!data) return null;

  const stats = {
    totalRaces: data.races?.length || 0,
    totalDrivers: data.drivers?.length || 0,
    totalCircuits: data.circuits?.length || 0,
    totalConstructors: data.constructors?.length || 0,
    seasonsCovered: data.seasons?.length || 0,
    dateRange: {
      start: null,
      end: null
    },
    topDrivers: [],
    topConstructors: []
  };

  // Calculate date range
  if (data.races && data.races.length > 0) {
    const years = data.races.map(race => parseInt(race.year || race.date?.split('-')[0]));
    stats.dateRange.start = Math.min(...years.filter(Boolean));
    stats.dateRange.end = Math.max(...years.filter(Boolean));
  }

  // Get top drivers by wins
  if (data.driver_standings && data.driver_standings.length > 0 && data.races) {
    // Create race ID to year mapping
    const raceIdToYear = {};
    data.races.forEach(race => {
      const year = parseInt(race.year || (race.date && race.date.split('-')[0]));
      if (year) raceIdToYear[race.raceId] = year;
    });

    // Group driver standings by driverId and year, taking the last race entry per year
    const driverYearStandings = {};
    data.driver_standings.forEach(standing => {
      const driverId = standing.driverId;
      const year = raceIdToYear[standing.raceId];
      if (!year) return;

      const key = `${driverId}-${year}`;
      if (!driverYearStandings[key]) {
        driverYearStandings[key] = { driverId, year, raceId: standing.raceId, points: 0, wins: 0 };
      }
      // Keep the entry with the highest raceId (last race of the year)
      if (standing.raceId > driverYearStandings[key].raceId) {
        driverYearStandings[key] = { driverId, year, raceId: standing.raceId, points: standing.points || 0, wins: standing.wins || 0 };
      }
    });

    // Sum points and wins across all years for each driver
    const driverStats = {};
    Object.values(driverYearStandings).forEach(({ driverId, points, wins }) => {
      if (!driverStats[driverId]) driverStats[driverId] = { points: 0, wins: 0 };
      driverStats[driverId].points += points;
      driverStats[driverId].wins += wins;
    });

    // Create driver ID to name mapping
    const driverNameMap = {};
    if (data.drivers) {
      data.drivers.forEach(driver => {
        const forename = driver.forename || '';
        const surname = driver.surname || '';
        const firstInitial = forename ? forename.charAt(0).toUpperCase() : '';
        driverNameMap[driver.driverId] = `${surname}, ${firstInitial}`;
      });
    }

    stats.topDrivers = Object.entries(driverStats)
      .sort(([,a], [,b]) => b.points - a.points)
      .slice(0, 10)
      .map(([driverId, stats]) => ({
        driverId,
        name: driverNameMap[driverId] || `Driver ${driverId}`,
        wins: stats.wins,
        points: stats.points
      }));
  }

  // Get top constructors by wins
  if (data.constructor_standings && data.constructor_standings.length > 0) {
    const constructorWins = {};
    data.constructor_standings.forEach(standing => {
      const constructorId = standing.constructorId;
      if (!constructorWins[constructorId]) constructorWins[constructorId] = { wins: 0, points: 0 };
      constructorWins[constructorId].wins += standing.wins || 0;
      constructorWins[constructorId].points += standing.points || 0;
    });
    
    stats.topConstructors = Object.entries(constructorWins)
      .sort(([,a], [,b]) => b.points - a.points)
      .slice(0, 10)
      .map(([constructorId, stats]) => ({
        constructorId,
        wins: stats.wins,
        points: stats.points
      }));
  }

  return stats;
};
